/**
 * Unschärfe und Schlagschatten der HTML-Box — EINE Auflösung für beide Editoren.
 *
 * DAS PROBLEM (Jans Fund 04.08.2026, im Code nachgewiesen): dieselben zwei
 * Effekte liegen an ZWEI verschiedenen Stellen, und jede Seite kannte nur ihre
 * eigene.
 *
 *   Template-Editor  schreibt  slide.blurBackground / slide.boxShadow
 *                              (pro Slide, die Knöpfe „Blur" / „Schatten")
 *   Pro-Editor       schreibt  options.backdropBlur / options.boxShadow
 *                              (Grundlagen → HTML-Boxen, global bzw. pro Slide)
 *
 *   Reader           las nur   slide.blurBackground / slide.boxShadow
 *   Pro-Editor-Vorschau las nur options.backdropBlur / options.boxShadow
 *
 * Folge: was im Pro-Editor eingestellt war, kam im VERÖFFENTLICHTEN Scrolly
 * nicht an — und was im Template-Editor eingestellt war, fehlte in der
 * Pro-Editor-Vorschau. Beide Seiten zeigten dem Nutzer einen Haken, der auf der
 * anderen Seite wirkungslos blieb.
 *
 * VORSICHT BEIM LESEN: `slide.boxShadow` und `options.boxShadow` heißen gleich
 * und sind verschiedene Felder. `slide.blurBackground` heißt dagegen anders als
 * `options.backdropBlur`, meint aber dasselbe.
 *
 * VORRANG: die Slide gewinnt vor den Optionen. Der Template-Editor-Knopf ist
 * ausdrücklich pro Slide gesetzt — er soll die globale Voreinstellung aus dem
 * Pro-Editor überstimmen können, in beide Richtungen (an wie aus). Nur wenn die
 * Slide gar nichts sagt (Feld fehlt), zählt die Option.
 */
export interface BoxEffectSlideLike {
    /**
     * Template-Editor, pro Slide.
     *
     * `null` ist ausdruecklich erlaubt: Altbestand fuehrt die Felder teils so,
     * und ein zu enger Typ hier hat den Pro-Editor schon einmal an genau diesen
     * Daten abstuerzen lassen (Jans Fund 04.08.2026). `null` zaehlt wie „nicht
     * gesetzt" — die Aussage macht nur ein echtes Boolean.
     */
    blurBackground?: boolean | null;
    /** Template-Editor, pro Slide — NICHT zu verwechseln mit options.boxShadow */
    boxShadow?: boolean | null;
}

export interface BoxEffectOptionsLike {
    /** Pro-Editor, Grundlagen → HTML-Boxen */
    backdropBlur?: boolean | null;
    /** Pro-Editor, Grundlagen → HTML-Boxen */
    boxShadow?: boolean | null;
}

export interface BoxEffects {
    blur: boolean;
    shadow: boolean;
}

export function resolveBoxEffects(
    slide: BoxEffectSlideLike | null | undefined,
    options: BoxEffectOptionsLike | null | undefined,
): BoxEffects {
    return {
        blur:
            typeof slide?.blurBackground === "boolean"
                ? slide.blurBackground
                : options?.backdropBlur === true,
        shadow:
            typeof slide?.boxShadow === "boolean"
                ? slide.boxShadow
                : options?.boxShadow === true,
    };
}

/**
 * Füllfarbe der HTML-Box unter Berücksichtigung der Unschärfe — EINE Regel
 * für Leser, Template-Editor-Vorschau UND Pro-Editor-Bühne.
 *
 * Ist die Unschärfe an, wird das Alpha der Füllung auf 0.85 gekappt: hinter
 * einer DECKENDEN Farbe ist backdrop-filter unsichtbar, der Nutzer hielte
 * „Blur" für kaputt. Darunter entscheidet der Transparenz-Regler (Jan
 * 21.10.2026: mit dem festen Deckel 0.3 wirkte die Box über dunklen Bildern
 * zu dunkel, und der Regler hatte bei Unschärfe keine Wirkung). Ohne Farbe + Blur greift der halbtransparente
 * Weiß-Rückfall alter PPI-Templates.
 *
 * Lag vorher als lokale Funktion im VisualStoryPlayer — die Pro-Editor-Bühne
 * (TextOverlayNormal) nahm die Farbe roh, deckendes Weiß schluckte dort das
 * Milchglas komplett (Jans Fund 06.08.2026: Template-Editor zeigt Milchglas,
 * Pro-Editor eine platte weiße Fläche, obwohl beide Haken gesetzt sind).
 */
/** Höchste Deckkraft der Box-Füllung bei Unschärfe (s. tintBackgroundForBlur). */
export const BLUR_DECKKRAFT_MAX = 0.85;

export function tintBackgroundForBlur(
    color: { r: number; g: number; b: number; a: number } | null | undefined,
    blurOn: boolean,
): string | undefined {
    if (!color) {
        return blurOn ? "rgba(255,255,255,0.15)" : undefined;
    }
    const alpha = blurOn ? Math.min(color.a, BLUR_DECKKRAFT_MAX) : color.a;
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}
